import { useMemo, useState, type FC } from 'react';
import type { HeroBasicProps } from './types';

import styled, { css } from 'styled-components';

import {
  defaultWebValue,
  filterBackgroundColor,
  imageViewer,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Wrapper, Button, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { HoverableDropzone } from '../HoverableDropzone';
import useGeneralActions from '../hooks/useGeneralActions';

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 55px 0 70px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    padding: 55px 0 70px;
    `}
`;

const StyledWrapper = styled(Wrapper)`
  position: relative;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 50px;
  max-width: 100%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    gap: 10px;
    `}
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 530px;
  height: auto;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      width: 480px;
      height: auto;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 360px;
      height: auto;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
      height: auto;
    `}

  ${({ mockup_id, theme: { $width } }) =>
    mockup_id === 1
      ? `
        width: 530px;
        height: auto;

        ${
          $width <= 1254 &&
          `
            left: -54px;
            width: 480px;
            height: auto;
          `
        }

        ${
          $width <= 991 &&
          `
            left: -40px;
            width: 360px;
            height: auto;
          `
        }

        ${
          $width <= 768 &&
          `
            left: -20px;
          `
        }

        ${
          $width <= 575 &&
          `
            width: 80%;
          `
        }
      `
      : mockup_id === 2
        ? `
        left: -90px;
        width: 500px;
        height: auto;

        ${
          $width <= 1254 &&
          `
            left: -64px;
            width: 448px;
            height: auto;
          `
        }

        ${
          $width <= 991 &&
          `
            left: -40px;
            width: 368px;
            height: auto;
          `
        }

        ${
          $width <= 768 &&
          `
            left: -20px;
          `
        }

        ${
          $width <= 575 &&
          `
            left: -10px;
            width: 80%;
            height: auto;
          `
        }
      `
        : mockup_id === 3
          ? `
        width: 440px;
        height: auto;

        ${
          $width <= 1254 &&
          `
            width: 402px;
            height: auto;
          `
        }

        ${
          $width <= 991 &&
          `
            width: 318px;
            height: auto;
          `
        }

        ${
          $width <= 575 &&
          `
            width: 70%;
            height: auto;
          `
        }
      `
          : ``}
`;

const TitleWrapper = styled.div`
  width: 655px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 492px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 26px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-top: 20px;
  `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  margin-bottom: 26px;
  font-weight: 700;
  line-height: 1.25em;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 28px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 20px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 58px;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 50px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 45px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 40px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const BtnInner = styled.div`
  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 415px;
  display: inline-block;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding-top: 24px;
      padding-bottom: 26px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 685px;
  width: 513px;
`;

const BookMediaImg = styled(ImageComp)<{ isImageUploaded: boolean }>`
  width: 100%;
  height: 100%;
  // object-fit: ${(props) => (props.isImageUploaded ? 'cover' : 'contain')};
  object-fit: cover;
  object-position: center center;
  border-radius: ${(props) => (props.isImageUploaded ? '5px' : '0px')};
`;

export const V4: FC<HeroBasicProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    customBackgroundColor,
    textColor,
    meta,
    hidden,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { pages, isCoverDisabled, bookMedia } = useBook();
  const { pagesThumbnails, purchaseElementId, updateImageMeta, type, isDashboard } = useSales();
  const { handleUploadAction } = useGeneralActions(props);

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, cover_thumbnail, pagesThumbnails]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      setIsImageUploaded(true);
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const handleUpload = async (file: File) => {
    setIsLoading(true);
    await handleUploadAction(file, setResize, 'book_mockup');
    setIsLoading(false);
  };

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || '#fff';
  }, [customBackgroundColor]);

  // Accessibility improvements
  const titleAccessKey = generateAccessKey('ht', order);
  const contentAccessKey = generateAccessKey('hc', order);
  const buttonAccessKey = generateAccessKey('hb', order);
  const skipLinkId = `skip-to-hero-${meta['accessorKey'] || 'hero'}`;

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  // Determine button type for accessibility
  const buttonType = meta['url']
    ? 'external link'
    : type === 'delivery'
      ? 'delivery action'
      : 'purchase button';

  return (
    <Hero
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: resolvedBackgroundColor
      }}
      aria-label="Hero section with left-aligned image and content"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <div role="img" aria-label="Book preview and mockup">
          {bookMockup ? (
            <HoverableDropzone
              isSalePage
              css={imageStyles as never}
              disabled={!editable}
              allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
              onUpload={async (file: File) => {
                try {
                  await handleUpload(file);
                } catch (error) {
                  handleUploadError(error);
                }
              }}
              loading={isLoading}
              alreadyUploaded={Boolean(bookMockup)}
              enableLinks
              updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
              imageFile={bookMockup}
              resizeSelector={editable}
              handleResize={({ width, height }) => {
                setResize({ width, height });
                updateImageMeta(meta['accessorKey'], width, 'width');
                updateImageMeta(meta['accessorKey'], height, 'height');
              }}
              initialPosition={{
                x: meta['x_1'] || 0,
                y: meta['y_1'] || 0,
                width: meta['d_width_1'],
                height: meta['d_height_1']
              }}
              meta={meta}
              positionEnabled
              handlePosition={(width: number, height: number, x: number, y: number) => {
                updateImageMeta(meta['accessorKey'], x, 'x_1');
                updateImageMeta(meta['accessorKey'], y, 'y_1');
                updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                updateImageMeta(meta['accessorKey'], height, 'd_height_1');
              }}
              imageAspectRatio={{
                ratio: '3x4',
                pixels: '600x800'
              }}
              ariaLabel="Book mockup image - drag and drop to upload new image">
              <BookMediaImg
                isImageUploaded={isImageUploaded}
                src={imageViewer(imageViewer(bookMockup)) || ''}
                alt="Book mockup preview showing cover design"
                width={resize.width || 500}
                height={resize.height || 500}
              />
            </HoverableDropzone>
          ) : (
            <StyledMockup
              mockup={mockup?.file}
              mockup_id={mockup?.id}
              cover_thumbnail={coverThumbnail}
              aria-label="Book mockup with cover thumbnail"
            />
          )}
        </div>

        <TitleWrapper id="HeadlineSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this hero title`,
                  tabIndex: 0
                })}
          />

          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim() ? 'Hero section description' : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this hero description`,
                  tabIndex: 0
                })}
          />

          <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
            {meta['url'] || type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            )}
          </BtnInner>
        </TitleWrapper>
      </StyledWrapper>
    </Hero>
  );
};

V4.displayName = 'HeroBasicV4';
